import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, ConfirmationModal } from '../components';
import { ScopedListBanner } from '../components/data-lists/ScopedListBanner';
import { ProductList } from '../components/products';
import { Button } from '../components/design-system';
import { countActive, FilterField, fv, ListFilterState, QuickView, relativeRange, SortOption, useListFilters } from '../components/listFilters/listFilters';
import { FilterSheet } from '../components/listFilters/FilterSheet';
import { ListFilterBar } from '../components/listFilters/ListFilterBar';
import { searchCategories, searchVendors } from '../components/listFilters/entitySearch';
import { useLocale } from '../contexts/LocaleContext';
import { EntityPickerModal } from '../components/EntityPickerModal';
import { apiService } from '../services/api';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useGoBack } from '../hooks/useGoBack';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { productsToCSV, generateEmptyProductCSVTemplate, downloadProductCSV } from '../utils/productCsvExport';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';

// Product fields filterable by their stored values ("More fields" in the filter
// sheet, next to custom fields) — matches PRODUCT_FIXED_FIELD_PATHS server-side.
const PRODUCT_VALUE_FIELDS = ['name', 'sku'];

/** API query for the products list / exports from the shared filter state. */
function productQuery(state: ListFilterState, sorts: SortOption[]): Record<string, any> {
  const f = state.filters;
  const sort = sorts.find((s) => s.key === state.sort) || sorts[0];
  return {
    search: fv.text(state.search),
    brand: fv.text(f.brand),
    categoryIds: fv.list(f.categories),
    vendorIds: f.withoutVendor === 'yes' ? 'none' : fv.csv(f.vendors),
    availability: fv.csv(f.availability),
    condition: fv.csv(f.condition),
    productTypes: fv.csv(f.productTypes),
    minPrice: fv.min(f.price),
    maxPrice: fv.max(f.price),
    stock: f.stock,
    hasVariants: f.hasVariants === 'yes' ? true : f.hasVariants === 'no' ? false : undefined,
    hasImage: f.hasImage,
    createdFrom: f.createdAt?.from ? `${f.createdAt.from}T00:00:00.000` : undefined,
    createdTo: f.createdAt?.to ? `${f.createdAt.to}T23:59:59.999` : undefined,
    sortBy: sort.sortBy,
    sortOrder: sort.sortOrder,
    customFieldFilters: fv.customFields(state.customFields),
  };
}

interface ProductsListScreenProps {
  catalogueId?: string;
  catalogueName?: string;
  /** Set when this screen is reached via a system Products Data List's scoped
   * mode (see DataListRecordsScreen.tsx) — restricts the list to only
   * records added THROUGH that list, and shows an exit banner back to it. */
  scopedToDataListId?: string;
  scopedListName?: string;
}

export const ProductsListScreen: React.FC<ProductsListScreenProps> = ({
  catalogueId: propCatalogueId,
  catalogueName = 'Products',
  scopedToDataListId,
  scopedListName,
}) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { currentBusiness } = useAppStore();
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const { t } = useLocale();
  const goBack = useGoBack('home');

  // Ensure we always have a valid catalogueId
  const catalogueId = propCatalogueId || 'default';

  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Filters — the shared list filters: remembered per screen, quick views + saved views.
  const quickViews = useMemo<QuickView[]>(() => [
    { key: 'low', label: t('products.view.lowStock'), icon: 'alert-circle-outline', filters: { stock: 'low' } },
    { key: 'out', label: t('products.view.outOfStock'), icon: 'close-circle-outline', filters: { stock: 'out' } },
    { key: 'noVendor', label: t('products.view.noVendor'), icon: 'business-outline', filters: { withoutVendor: 'yes' } },
    { key: 'noPhoto', label: t('products.view.noPhoto'), icon: 'image-outline', filters: { hasImage: 'no' } },
    { key: 'new', label: t('products.view.new'), icon: 'sparkles-outline', filters: { createdAt: relativeRange.days(30) } },
  ], [t]);
  const list = useListFilters(scopedToDataListId ? `products:${scopedToDataListId}` : `products:${catalogueId}`, { quickViews });
  const [showFilters, setShowFilters] = useState(false);
  const productFields = useMemo<FilterField[]>(() => [
    { key: 'categories', label: t('products.filter.categories'), type: 'entity', search: searchCategories, section: t('products.filterSection.catalog') },
    { key: 'vendors', label: t('products.filter.vendors'), type: 'entity', search: searchVendors, section: t('products.filterSection.catalog') },
    { key: 'withoutVendor', label: t('products.filter.withoutVendor'), type: 'choice', section: t('products.filterSection.catalog'), options: [{ value: 'yes', label: t('products.filter.withoutVendorYes') }] },
    { key: 'brand', label: t('products.filter.brand'), type: 'text', section: t('products.filterSection.catalog') },
    { key: 'productTypes', label: t('products.filter.type'), type: 'multi', section: t('products.filterSection.catalog'), options: ['physical', 'digital', 'service'].map((v) => ({ value: v, label: t(`products.type.${v}` as any) })) },
    { key: 'stock', label: t('products.filter.stock'), type: 'choice', section: t('products.filterSection.stock'), options: [{ value: 'in', label: t('products.stock.in') }, { value: 'low', label: t('products.stock.low') }, { value: 'out', label: t('products.stock.out') }] },
    { key: 'availability', label: t('products.filter.availability'), type: 'multi', section: t('products.filterSection.stock'), options: ['in_stock', 'out_of_stock', 'preorder', 'backorder'].map((v) => ({ value: v, label: t(`products.availability.${v}` as any) })) },
    { key: 'price', label: t('products.filter.price'), type: 'range', section: t('products.filterSection.details') },
    { key: 'condition', label: t('products.filter.condition'), type: 'multi', section: t('products.filterSection.details'), options: ['new', 'refurbished', 'used'].map((v) => ({ value: v, label: t(`products.condition.${v}` as any) })) },
    { key: 'hasVariants', label: t('products.filter.variants'), type: 'choice', section: t('products.filterSection.details'), options: [{ value: 'yes', label: t('products.filter.withVariants') }, { value: 'no', label: t('products.filter.noVariants') }] },
    { key: 'hasImage', label: t('products.filter.photo'), type: 'choice', section: t('products.filterSection.details'), options: [{ value: 'yes', label: t('products.filter.withPhoto') }, { value: 'no', label: t('products.filter.noPhoto') }] },
    { key: 'createdAt', label: t('products.filter.added'), type: 'date', section: t('products.filterSection.details') },
  ], [t]);
  const productSorts = useMemo<SortOption[]>(() => [
    { key: 'newest', label: t('products.sort.newest'), sortBy: 'createdAt', sortOrder: 'desc', icon: 'arrow-down-outline' },
    { key: 'name', label: t('products.sort.name'), sortBy: 'name', sortOrder: 'asc', icon: 'text-outline' },
    { key: 'priceAsc', label: t('products.sort.priceAsc'), sortBy: 'price', sortOrder: 'asc', icon: 'trending-up-outline' },
    { key: 'priceDesc', label: t('products.sort.priceDesc'), sortBy: 'price', sortOrder: 'desc', icon: 'trending-down-outline' },
    { key: 'stockAsc', label: t('products.sort.stockAsc'), sortBy: 'inventoryQuantity', sortOrder: 'asc', icon: 'cube-outline' },
    { key: 'updated', label: t('products.sort.updated'), sortBy: 'updatedAt', sortOrder: 'desc', icon: 'time-outline' },
  ], [t]);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [cataloguesMap, setCataloguesMap] = useState<Map<string, { _id: string; name: string }>>(new Map());
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  // Sort/filter — see VendorsListScreen.tsx for the reference wiring.
  const [customFieldKeys, setCustomFieldKeys] = useState<string[]>([]);
  const customFieldsConfig = useMemo(() => ({
    fields: [...PRODUCT_VALUE_FIELDS.map((key) => ({ key, label: t(`products.field.${key}` as any) })), ...customFieldKeys.map((key) => ({ key, label: key }))],
    loadValues: async (key: string) => (await apiService.getProductCustomFieldValues(key)).data || [],
  }), [customFieldKeys, t]);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [showVendorPicker, setShowVendorPicker] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev);
    setSelectedProductIds([]);
  }, []);

  const toggleProductSelection = useCallback((productId: string) => {
    setSelectedProductIds((prev) => (prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]));
  }, []);

  const handleBulkAddToVendor = useCallback(async (vendorIds: string[]) => {
    setShowVendorPicker(false);
    setBulkActionLoading(true);
    try {
      const res = await apiService.bulkLinkProducts({ productIds: selectedProductIds, addVendorIds: vendorIds });
      if (res.statusCode === 1000) {
        showNotification('success', 'Added', `${selectedProductIds.length} product(s) added to vendor`);
        setSelectionMode(false);
        setSelectedProductIds([]);
        loadProducts(0, true);
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to add products to vendor');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to add products to vendor');
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedProductIds]);

  const handleBulkAddToCategory = useCallback(async (catalogueIds: string[]) => {
    setShowCategoryPicker(false);
    setBulkActionLoading(true);
    try {
      const res = await apiService.bulkLinkProducts({ productIds: selectedProductIds, addCatalogueIds: catalogueIds });
      if (res.statusCode === 1000) {
        showNotification('success', 'Added', `${selectedProductIds.length} product(s) added to category`);
        setSelectionMode(false);
        setSelectedProductIds([]);
        loadProducts(0, true);
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to add products to category');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to add products to category');
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedProductIds]);

  useEffect(() => {
    apiService.getProductCustomFieldKeys().then((res) => {
      if (res.statusCode === 1000 && res.data) setCustomFieldKeys(res.data);
    }).catch(() => {});
  }, []);

  const loadProducts = useCallback(
    async (page = 0, reset = false) => {
      try {
        setError(null);
        if (reset) {
          setLoading(true);
        }

        if (!catalogueId) {
          throw new Error('Catalogue ID is required');
        }

        const query: any = {
          limit: 20,
          skip: page * 20,
        };

        if (scopedToDataListId) query.dataListId = scopedToDataListId;
        Object.assign(query, productQuery(list.state, productSorts));

        console.log('🔍 [ProductsListScreen] Loading products:', {
          catalogueId,
          query,
          businessId: currentBusiness?._id,
        });

        const response = await apiService.getProductsByCatalogue(catalogueId, query);

        console.log('🔍 [ProductsListScreen] Products response:', {
          statusCode: response.statusCode,
          productsCount: response.data?.products?.length || 0,
          total: response.data?.pagination?.total || 0,
          errors: response.errors,
        });

        if (response.statusCode === 1000 && response.data) {
          const newProducts = response.data.products || [];
          console.log('🔍 [ProductsListScreen] Setting products:', newProducts.length);
          
          // Load catalogue info for products — a product may now belong to
          // multiple catalogues (catalogueIds array), so collect the union of
          // every catalogue id referenced across the page's products.
          const catalogueIds = [...new Set(newProducts.flatMap((p: any) => p.catalogueIds || []).filter(Boolean))];
          if (catalogueIds.length > 0) {
            try {
              const cataloguesResponse = await apiService.getCatalogues();
              if (cataloguesResponse.statusCode === 1000 && cataloguesResponse.data) {
                const catalogues = cataloguesResponse.data.catalogues || [];
                const newMap = new Map(cataloguesMap);
                catalogues.forEach((cat: any) => {
                  if (cat._id) {
                    newMap.set(cat._id, { _id: cat._id, name: cat.name });
                  }
                });
                setCataloguesMap(newMap);

                // Enrich products with catalogue info — one entry per catalogue the product belongs to.
                newProducts.forEach((product: any) => {
                  product.catalogues = (product.catalogueIds || [])
                    .filter((id: string) => newMap.has(id))
                    .map((id: string) => newMap.get(id)!);
                });
              }
            } catch (err) {
              console.error('Error loading catalogues:', err);
            }
          }
          
          setProducts((prev) => (reset ? newProducts : [...prev, ...newProducts]));
          setTotal(response.data.pagination?.total || 0);
          setHasMore(newProducts.length === 20);
          setCurrentPage(page);
        } else {
          const errorMsg = response.errors?.[0]?.message || 'Failed to load products';
          console.error('❌ [ProductsListScreen] Error loading products:', errorMsg, response.errors);
          setError(errorMsg);
          if (reset) {
            setProducts([]);
            setTotal(0);
          }
        }
      } catch (error: any) {
        console.error('Error loading products:', error);
        const errorMsg = error.message || 'Failed to load products';
        setError(errorMsg);
        if (reset) {
          setProducts([]);
          setTotal(0);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [catalogueId, list.state, scopedToDataListId, productSorts]
  );

  // Reload whenever the filters change (search debounced), once remembered filters are read.
  const filterKey = JSON.stringify([list.state.filters, list.state.sort, list.state.customFields, list.state.search]);
  useEffect(() => {
    if (!catalogueId || !list.ready) return;
    const timer = setTimeout(() => loadProducts(0, true), list.state.search ? 450 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogueId, scopedToDataListId, list.ready, filterKey]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadProducts(0, true);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore && !error) {
      loadProducts(currentPage + 1, false);
    }
  };

  const handleProductPress = (productId: string) => {
    // Filters are remembered by the screen itself (useListFilters).
    const params = { scopedToDataListId, scopedListName };
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.history.replaceState({ ...window.history.state, screen: 'products-list', conversationId: catalogueId, additionalParams: params }, '', window.location.href);
    }
    navigateTo('product-detail', productId, { productReturnTo: { catalogueId, params } });
  };

  const handleProductEdit = (productId: string) => {
    navigateTo('product-edit', productId, scopedToDataListId ? { listId: scopedToDataListId } : undefined);
  };

  const handleProductDelete = (productId: string) => {
    setProductToDelete(productId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteProduct(productToDelete);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Product deleted successfully');
        setShowDeleteModal(false);
        setProductToDelete(null);
        loadProducts(0, true);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to delete product';
        showNotification('error', 'Error', errorMessage);
        setShowDeleteModal(false);
        setProductToDelete(null);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to delete product');
      setShowDeleteModal(false);
      setProductToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateProduct = () => {
    navigateTo('product-create', catalogueId, scopedToDataListId ? { listId: scopedToDataListId } : undefined);
  };

  const handleBulkImport = () => {
    navigateTo('product-bulk-import', catalogueId, scopedToDataListId ? { listId: scopedToDataListId } : undefined);
  };

  const handleDownloadCSV = async () => {
    try {
      setDownloadingCSV(true);
      
      // Fetch all products for the catalogue using the same method as loadProducts
      let allProducts: any[] = [];
      let page = 0;
      let hasMore = true;
      
      // Build query with filters (same as loadProducts)
      const buildQuery = (pageNum: number) => {
        const query: any = {
          limit: 100, // Use larger limit for export
          skip: pageNum * 100,
        };
        
        Object.assign(query, productQuery(list.state, productSorts));
        if (scopedToDataListId) query.dataListId = scopedToDataListId;
        
        return query;
      };
      
      while (hasMore) {
        const query = buildQuery(page);
        const response = await apiService.getProductsByCatalogue(catalogueId, query);
        
        console.log('🔍 [ProductsListScreen] Download CSV - Page', page, 'Response:', {
          statusCode: response.statusCode,
          hasData: !!response.data,
          productsCount: response.data?.products?.length || 0,
          total: response.data?.pagination?.total || 0,
        });
        
        if (response.statusCode === 1000 && response.data) {
          const pageProducts = response.data.products || [];
          console.log('🔍 [ProductsListScreen] Download CSV - Found', pageProducts.length, 'products on page', page);
          
          if (pageProducts.length > 0) {
            allProducts = [...allProducts, ...pageProducts];
          }
          
          // Check if there are more pages
          const total = response.data.pagination?.total || 0;
          hasMore = pageProducts.length === 100 && allProducts.length < total;
          page++;
        } else {
          console.warn('🔍 [ProductsListScreen] Download CSV - No more products or error:', response);
          hasMore = false;
        }
      }
      
      console.log('🔍 [ProductsListScreen] Download CSV - Total products collected:', allProducts.length);
      
      // Convert to CSV
      let csvContent: string;
      if (allProducts.length > 0) {
        csvContent = productsToCSV(allProducts);
        console.log('✅ [ProductsListScreen] Download CSV - Generated CSV with', allProducts.length, 'products');
      } else {
        // Generate empty template if no products
        console.log('⚠️ [ProductsListScreen] Download CSV - No products found, generating template');
        csvContent = generateEmptyProductCSVTemplate();
      }
      
      // Download CSV
      const filename = allProducts.length > 0 
        ? `products_${new Date().toISOString().split('T')[0]}.csv`
        : `products_template.csv`;
      downloadProductCSV(csvContent, filename);
      
      if (allProducts.length > 0) {
        showNotification('success', 'Success', `Exported ${allProducts.length} product${allProducts.length > 1 ? 's' : ''} to CSV successfully!`);
      } else {
        showNotification('info', 'Template Downloaded', 'No products found. Downloaded empty template. You can fill it and upload.');
      }
    } catch (error: any) {
      console.error('❌ [ProductsListScreen] Error downloading products CSV:', error);
      showNotification('error', 'Error', error.message || 'Failed to download products CSV');
    } finally {
      setDownloadingCSV(false);
    }
  };

  const handleExportProducts = async (format: 'csv' | 'excel' = 'csv') => {
    if (format === 'csv') {
      await handleDownloadCSV();
      return;
    }
    
    // Excel export
    try {
      setDownloadingExcel(true);
      showNotification('info', 'Exporting', `Exporting products to ${format.toUpperCase()}...`);
      
      // Build filters for export
      const exportFilters: any = productQuery(list.state, productSorts);

      const blob = await apiService.exportProducts(catalogueId, format, exportFilters);
      
      // Download file
      if (Platform.OS === 'web') {
        // Web: Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `products_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Mobile: Use expo-file-system or similar
        // For now, show success message
        showNotification('success', 'Exported', `Products exported successfully! Check your downloads folder.`);
      }
      
      showNotification('success', 'Success', `Products exported to ${format.toUpperCase()} successfully!`);
    } catch (error: any) {
      console.error('Error exporting products:', error);
      showNotification('error', 'Error', error.message || 'Failed to export products');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const activeFiltersCount = countActive(productFields, list.state);

  const renderContent = () => {
    if (loading && products.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>Loading products...</Text>
        </View>
      );
    }

    if (error && products.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorTitle}>Unable to Load Products</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Button
            title="Try Again"
            onPress={() => loadProducts(0, true)}
            variant="primary"
            style={styles.retryButton}
          />
        </View>
      );
    }

    return (
      <View style={styles.listContainer}>
        <ProductList
          products={products}
          loading={false}
          refreshing={refreshing}
          onRefresh={handleRefresh}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          onProductPress={handleProductPress}
          onProductEdit={handleProductEdit}
          onProductDelete={handleProductDelete}
          showActions={!selectionMode}
          emptyMessage="No products yet. Create your first product to get started!"
          containerStyle={styles.listContent}
          selectionMode={selectionMode}
          selectedIds={selectedProductIds}
          onToggleSelect={toggleProductSelection}
        />
      </View>
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        /* Enhanced Header — fixed, single row, same height as every other
            list screen regardless of how many actions Products logically
            has. Filter stays as a direct icon button (most-used secondary
            action); scanner/download/export/import/Catalogues all collapse
            into the overflow "..." menu; Add stays the primary button. */
        <FormHeader
          title={scopedToDataListId ? (scopedListName || 'Products') : 'Products'}
          subtitle={
            !loading
              ? `${total} ${total === 1 ? 'product' : 'products'}${activeFiltersCount > 0 ? ` • ${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''}` : ''}`
              : undefined
          }
          onBack={goBack}
          actions={[
            { icon: selectionMode ? 'close' : 'checkbox-outline', onPress: toggleSelectionMode, active: selectionMode, label: selectionMode ? 'Cancel Selection' : 'Select', accessibilityLabel: selectionMode ? 'Cancel selection' : 'Select products' },
            { icon: 'barcode-outline', onPress: () => navigateTo('product-scanner', catalogueId), label: 'Scan Barcode', accessibilityLabel: 'Scan barcode' },
            ...(!scopedToDataListId ? [{ icon: 'cube-outline' as const, onPress: () => navigateTo('catalogues'), label: 'Catalogues', accessibilityLabel: 'Catalogues' }] : []),
            ...(Platform.OS === 'web'
              ? ([
                  { icon: 'document-text-outline', onPress: handleDownloadCSV, loading: downloadingCSV, label: 'Download CSV', accessibilityLabel: 'Download CSV' },
                  { icon: 'grid-outline', onPress: () => handleExportProducts('excel'), loading: downloadingExcel, label: 'Export Excel', accessibilityLabel: 'Export Excel' },
                  { icon: 'cloud-upload-outline', onPress: handleBulkImport, label: 'Bulk Import', accessibilityLabel: 'Bulk import' },
                ] as const)
              : []),
            { icon: 'add', primary: true, onPress: handleCreateProduct, label: 'Add product', accessibilityLabel: 'Add product' },
          ]}
        />
      }
    >
      <View style={styles.container}>
        {scopedToDataListId && (
          /* Scoped mode — this list is showing only records added through a
             System Data List. Body content, not header chrome (it's
             conditional/temporary, unlike the header's fixed row). */
          <View style={styles.scopedBannerWrapper}>
            <ScopedListBanner
              moduleLabel="Products"
              moduleIcon="cube-outline"
              moduleScreen="products-list"
              moduleScreenParam="default"
              moduleScreenRouteParams={{}}
            />
          </View>
        )}

        <ListFilterBar
          controller={list}
          fields={productFields}
          sorts={productSorts}
          onOpenFilters={() => setShowFilters(true)}
          searchPlaceholder={t('products.filter.search')}
          customFieldLabels={Object.fromEntries(customFieldsConfig.fields.map((f) => [f.key, f.label]))}
        />

        {/* Content */}
        {renderContent()}

        <FilterSheet
          visible={showFilters}
          title={t('products.filter.title')}
          fields={productFields}
          sorts={productSorts}
          value={list.state}
          customFields={customFieldsConfig}
          onApply={list.apply}
          onClose={() => setShowFilters(false)}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          visible={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setProductToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Product"
          message="Are you sure you want to delete this product? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="error"
          loading={deleting}
        />

        {selectionMode && selectedProductIds.length > 0 && (
          <View style={styles.bulkActionBar}>
            <Text style={styles.bulkActionCount}>{selectedProductIds.length} selected</Text>
            <View style={styles.bulkActionButtons}>
              <TouchableOpacity style={styles.bulkActionButton} onPress={() => setShowVendorPicker(true)} disabled={bulkActionLoading}>
                <Ionicons name="business-outline" size={16} color={colors.white} />
                <Text style={styles.bulkActionButtonText}>Add to Vendor</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.bulkActionButton} onPress={() => setShowCategoryPicker(true)} disabled={bulkActionLoading}>
                <Ionicons name="pricetag-outline" size={16} color={colors.white} />
                <Text style={styles.bulkActionButtonText}>Add to Category</Text>
              </TouchableOpacity>
            </View>
            {bulkActionLoading && <ActivityIndicator size="small" color={colors.white} style={styles.bulkActionLoading} />}
          </View>
        )}

        <EntityPickerModal
          visible={showVendorPicker}
          onClose={() => setShowVendorPicker(false)}
          onConfirm={handleBulkAddToVendor}
          title="Add to Vendor"
          confirmText="Add"
          fetchItems={async ({ search, skip, limit }) => {
            const res = await apiService.getVendors({ search: search || undefined, skip, limit });
            const vendors = res.data?.vendors || [];
            const total = res.data?.pagination?.total || 0;
            return { items: vendors.map((v: any) => ({ id: v._id, label: v.name })), hasMore: skip + vendors.length < total };
          }}
        />

        <EntityPickerModal
          visible={showCategoryPicker}
          onClose={() => setShowCategoryPicker(false)}
          onConfirm={handleBulkAddToCategory}
          title="Add to Category"
          confirmText="Add"
          fetchItems={async ({ search, skip, limit }) => {
            const res = await apiService.getCatalogues({ search: search || undefined, skip, limit });
            const catalogues = res.data?.catalogues || [];
            const total = res.data?.pagination?.total || 0;
            return { items: catalogues.map((c: any) => ({ id: c._id, label: c.name })), hasMore: skip + catalogues.length < total };
          }}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  searchContainer: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingTop: spacing.md },
  searchInput: { flex: 1, marginBottom: 0 },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  scopedBannerWrapper: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
  },
  bulkActionBar: {
    position: 'absolute',
    left: spacing.lg,
    right: spacing.lg,
    bottom: spacing.lg,
    backgroundColor: colors.text.primary,
    borderRadius: spacing.borderRadius.lg,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...designTokens.shadows.md,
  },
  bulkActionCount: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
  },
  bulkActionButtons: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.primary,
  },
  bulkActionButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
  },
  bulkActionLoading: {
    marginLeft: spacing.sm,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
  },
  centerText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  errorTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  errorText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.xl,
    fontFamily: typography.fontFamily.regular,
  },
  retryButton: {
    minWidth: 120,
  },
  listContainer: {
    flex: 1,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
});
